import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  anonymizePair,
  BlindVote,
  BlindWinner,
  createBlindVote,
  RubricScores,
} from './blindReviewModel';
import './BlindReviewWorkbench.css';
import { checkpointRubric, QUALITY_CHECKPOINTS, QualityCheckpointId, qualityArtifactPath } from './evaluationPack';
import { API_BASE } from './apiBase';

const STORAGE_KEY = 'morpho.blind-review.v1';
const RUBRICS: Array<[keyof RubricScores, string, string]> = [
  ['hook', '钩子与追看', '开场承诺、段尾悬念和继续观看欲望'],
  ['causality', '因果与节奏', '行动导致后果，升级清晰且无无效拖延'],
  ['character', '人物与情感', '欲望明确、选择可信、情绪兑现充分'],
  ['devices', '装置与兑现', '伏笔、反转、发现等服务剧情且得到兑现'],
  ['producibility', '可拍摄性', '场景、对白与动作具备实际制作可行性'],
];

const initialScores: RubricScores = {
  hook: 3,
  causality: 3,
  character: 3,
  devices: 3,
  producibility: 3,
};

function download(name: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function BlindReviewWorkbench() {
  const [reviewId, setReviewId] = useState(() => `review-${new Date().toISOString().slice(0, 10)}`);
  const [checkpoint, setCheckpoint] = useState<QualityCheckpointId>('brief');
  const [artifactId, setArtifactId] = useState('');
  const [qualityReport, setQualityReport] = useState<any>(null);
  const [artifactError, setArtifactError] = useState('');
  const [firstText, setFirstText] = useState('');
  const [secondText, setSecondText] = useState('');
  const [scores, setScores] = useState<RubricScores>(initialScores);
  const [winner, setWinner] = useState<BlindWinner>('abstain');
  const [note, setNote] = useState('');
  const [votes, setVotes] = useState<BlindVote[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  });
  const pair = useMemo(
    () => anonymizePair(
      { id: 'first', title: '', text: firstText },
      { id: 'second', title: '', text: secondText },
      reviewId,
    ),
    [firstText, reviewId, secondText],
  );

  const saveVote = () => {
    const nextVotes = [...votes, createBlindVote(reviewId, winner, scores, note)];
    setVotes(nextVotes);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextVotes));
    setNote('');
  };

  const exportCsv = () => {
    const header = 'review_id,winner,total,hook,causality,character,devices,producibility,note,recorded_at';
    const rows = votes.map((vote) => [
      vote.reviewId, vote.winner, vote.total, vote.scores.hook, vote.scores.causality,
      vote.scores.character, vote.scores.devices, vote.scores.producibility,
      JSON.stringify(vote.note), vote.recordedAt,
    ].join(','));
    download('morpho-blind-review.csv', [header, ...rows].join('\n'), 'text/csv;charset=utf-8');
  };

  return (
    <main className="blind-review">
      <header className="blind-review__header">
        <div>
          <a href="/" className="blind-review__back">← 返回 Morpho 工作台</a>
          <p className="blind-review__eyebrow">ZERO-TOKEN EVALUATION</p>
          <h1>分阶段质量评估</h1>
          <p>不必等完整成稿：逐步检查简报、人物、单集、场景和关键场；本页不请求任何模型 API。</p>
        </div>
        <div className="blind-review__summary"><strong>{votes.length}</strong><span>已记录判断</span></div>
      </header>

      <section className="blind-review__setup">
        <label>当前检查点<select value={checkpoint} onChange={(event) => setCheckpoint(event.target.value as QualityCheckpointId)}>{QUALITY_CHECKPOINTS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
        <label>评审编号<input value={reviewId} onChange={(event) => setReviewId(event.target.value)} /></label>
        <p>{QUALITY_CHECKPOINTS.find((item) => item.id === checkpoint)?.question}</p>
      </section>

      <section className="blind-review__checkpoint-rubric">
        <strong>本阶段适用指标</strong>
        <div>{checkpointRubric(checkpoint).map((item) => <span key={item}>{item.replace(/_/g, ' ')}</span>)}</div>
      </section>

      <section className="blind-review__artifact-loader">
        <div><strong>载入自动质量报告</strong><span>从生成运行的 runtime evidence 复制 artifact ID；只读取，不触发生成。</span></div>
        <input value={artifactId} onChange={(event) => setArtifactId(event.target.value)} placeholder="artifact_…" />
        <button disabled={!artifactId.trim()} onClick={async () => {
          try {
            setArtifactError('');
            const response = await axios.get(`${API_BASE}${qualityArtifactPath(artifactId)}`);
            setQualityReport(response.data);
          } catch (error: any) {
            setArtifactError(error?.response?.data?.error || error?.message || '报告读取失败');
          }
        }}>读取报告</button>
      </section>
      {(qualityReport || artifactError) && <section className="blind-review__quality-report">
        {artifactError ? <p>{artifactError}</p> : <>
          <header><strong>{qualityReport?.payload?.level || qualityReport?.kind}</strong><b>{qualityReport?.payload?.navigation_score ?? '—'}</b></header>
          {(qualityReport?.payload?.findings || []).map((item: any) => <div key={`${item.code}-${item.evidence_address}`}><span>{item.severity} · {Math.round((item.confidence || 0) * 100)}%</span><strong>{item.message}</strong><code>{item.evidence_address}</code></div>)}
        </>}
      </section>}

      <section className="blind-review__texts">
        {pair.map((candidate, index) => (
          <article key={candidate.anonymousId}>
            <h2>{candidate.label}</h2>
            <textarea
              aria-label={candidate.label}
              value={index === 0 ? candidate.text : candidate.text}
              placeholder="粘贴候选剧本文本……"
              onChange={(event) => {
                const isFirstSource = anonymizePair(
                  { id: 'first', title: '', text: 'FIRST' },
                  { id: 'second', title: '', text: 'SECOND' },
                  reviewId,
                )[index].text === 'FIRST';
                if (isFirstSource) setFirstText(event.target.value);
                else setSecondText(event.target.value);
              }}
            />
          </article>
        ))}
      </section>

      <section className="blind-review__rubric">
        <div><p className="blind-review__eyebrow">STRUCTURED JUDGMENT</p><h2>五维量表</h2></div>
        <div className="blind-review__rubric-grid">
          {RUBRICS.map(([key, label, hint]) => (
            <label key={key}>
              <span><strong>{label}</strong><small>{hint}</small></span>
              <input type="range" min="1" max="5" value={scores[key]} onChange={(event) => setScores({ ...scores, [key]: Number(event.target.value) })} />
              <b>{scores[key]}</b>
            </label>
          ))}
        </div>
      </section>

      <section className="blind-review__decision">
        <label>总体判断
          <select value={winner} onChange={(event) => setWinner(event.target.value as BlindWinner)}>
            <option value="abstain">证据不足 / 弃权</option><option value="A">文本 A 更优</option>
            <option value="B">文本 B 更优</option><option value="tie">基本持平</option>
          </select>
        </label>
        <label>证据备注<textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="指出具体段落、装置或因果问题" /></label>
        <button onClick={saveVote} disabled={!firstText.trim() || !secondText.trim()}>保存本次判断</button>
      </section>

      <footer className="blind-review__footer">
        <span>数据只保存在当前浏览器，可导出后纳入论文评估。</span>
        <div><button onClick={() => download('morpho-blind-review.json', JSON.stringify(votes, null, 2), 'application/json')}>导出 JSON</button><button onClick={exportCsv}>导出 CSV</button></div>
      </footer>
    </main>
  );
}
